import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Edit,
  Download,
  Users,
  Activity,
  ArrowLeft,
  TrendingUp,
  Settings2,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../../components/ui/tabs';
import PageHeader from '../../../components/common/PageHeader';
import StatsCard from '../../../components/common/StatsCard';
import StatusChip from '../components/shared/StatusChip';
import InfoCard from '../../../components/common/InfoCard';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import { DataTable } from '../../../components/ui/data-table';
import FundPerformanceChart from '../components/dashboard/FundPerformanceChart';
import {
  pammFundService,
  pammInvestorService,
  pammTradeService,
  pammAllocationService,
  pammReportService,
} from '../services/pammService';
import {
  formatCurrency,
  formatPercent,
  formatDate,
  formatDateTime,
  getProfitColor,
  exportToCSV,
} from '../utils';
import type {
  PAMMFund,
  PAMMInvestor,
  PAMMTrade,
  AllocationRecord,
  PAMMReport,
} from '../types';
import { PAMM_ROUTES } from '../../../constants/routeConstants';
import { MOCK_FUND_PERFORMANCE } from '../mock';
import type { DataTableColumn } from '../../../components/ui/data-table';

export default function FundDetails() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [fund, setFund] = useState<PAMMFund | null>(null);
  const [investors, setInvestors] = useState<PAMMInvestor[]>([]);
  const [trades, setTrades] = useState<PAMMTrade[]>([]);
  const [allocations, setAllocations] = useState<AllocationRecord[]>([]);
  const [reports, setReports] = useState<PAMMReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInvestors, setSearchInvestors] = useState('');
  const [searchTrades, setSearchTrades] = useState('');
  const [searchReports, setSearchReports] = useState('');
  const [searchAllocations, setSearchAllocations] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const [fundData, invData, tradeData, allocData, rptData] =
        await Promise.all([
          pammFundService.getById(id),
          pammInvestorService.getByFund(id),
          pammTradeService.getByFund(id),
          pammAllocationService.getByFund(id),
          pammReportService.getAll(),
        ]);
      if (!fundData) throw new Error('Fund not found');
      setFund(fundData);
      setInvestors(invData);
      setTrades(tradeData);
      setAllocations(allocData);
      setReports(rptData.filter(r => r.fundId === id));
    } catch {
      setError('Failed to load fund details.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="space-y-4">
        <PageHeader title="Fund Details" />
        <LoadingSkeleton type="detail" />
      </div>
    );
  }

  if (error || !fund) {
    return (
      <div className="space-y-4">
        <PageHeader title="Fund Details" />
        <PAMMErrorState message={error ?? 'Fund not found.'} onRetry={load} />
      </div>
    );
  }

  const investorCols: DataTableColumn<PAMMInvestor>[] = [
    {
      id: 'name',
      header: 'Investor',
      accessor: row => (
        <div>
          <p className="font-medium text-xs">{row.name}</p>
          <p className="text-xs text-muted-foreground">{row.email}</p>
        </div>
      ),
    },
    {
      id: 'invested',
      header: 'Invested',
      accessor: row => (
        <span className="text-xs font-medium">
          {formatCurrency(row.investedAmount)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.investedAmount,
    },
    {
      id: 'value',
      header: 'Current Value',
      accessor: row => (
        <span className="text-xs font-medium">
          {formatCurrency(row.currentValue)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.currentValue,
    },
    {
      id: 'profit',
      header: 'Profit',
      accessor: row => (
        <span className={`text-xs font-semibold ${getProfitColor(row.profit)}`}>
          {formatCurrency(row.profit)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.profit,
    },
    {
      id: 'alloc',
      header: 'Allocation',
      accessor: row => (
        <span className="text-xs">{row.allocationPercentage.toFixed(2)}%</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <StatusChip status={row.status} size="sm" />,
    },
    {
      id: 'actions',
      header: '',
      accessor: row => (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() =>
            navigate(`${PAMM_ROUTES.PAMM_INVESTOR_DETAILS}/${row.id}`)
          }
        >
          View
        </Button>
      ),
    },
  ];

  const tradeCols: DataTableColumn<PAMMTrade>[] = [
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: row => (
        <span className="font-medium text-xs">{row.symbol}</span>
      ),
      sortable: true,
      sortValue: row => row.symbol,
    },
    {
      id: 'dir',
      header: 'Direction',
      accessor: row => <StatusChip status={row.direction} size="sm" />,
    },
    {
      id: 'volume',
      header: 'Volume',
      accessor: row => <span className="text-xs">{row.volume} lots</span>,
      sortable: true,
      sortValue: row => row.volume,
    },
    {
      id: 'openPrice',
      header: 'Open',
      accessor: row => (
        <span className="text-xs">{row.openPrice.toFixed(5)}</span>
      ),
    },
    {
      id: 'closePrice',
      header: 'Close',
      accessor: row => (
        <span className="text-xs">
          {row.closePrice ? row.closePrice.toFixed(5) : '—'}
        </span>
      ),
    },
    {
      id: 'profit',
      header: 'P&L',
      accessor: row => (
        <span
          className={`text-xs font-semibold ${getProfitColor(row.profit ?? 0)}`}
        >
          {row.profit != null ? formatCurrency(row.profit) : '—'}
        </span>
      ),
      sortable: true,
      sortValue: row => row.profit ?? 0,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <StatusChip status={row.status} size="sm" />,
    },
    {
      id: 'openTime',
      header: 'Opened',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {formatDateTime(row.openTime)}
        </span>
      ),
    },
  ];

  const allocCols: DataTableColumn<AllocationRecord>[] = [
    {
      id: 'investor',
      header: 'Investor',
      accessor: row => (
        <span className="text-xs font-medium">{row.investorName}</span>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      accessor: row => <StatusChip status={row.type} size="sm" />,
    },
    {
      id: 'prev',
      header: 'Previous',
      accessor: row => (
        <span className="text-xs">{row.previousAllocation.toFixed(2)}%</span>
      ),
    },
    {
      id: 'new',
      header: 'New',
      accessor: row => (
        <span className="text-xs font-medium">
          {row.newAllocation.toFixed(2)}%
        </span>
      ),
    },
    {
      id: 'amount',
      header: 'Amount',
      accessor: row => (
        <span className="text-xs">{formatCurrency(row.amount)}</span>
      ),
    },
    {
      id: 'date',
      header: 'Date',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {formatDate(row.date)}
        </span>
      ),
    },
    {
      id: 'by',
      header: 'Processed By',
      accessor: row => (
        <span className="text-xs text-muted-foreground">{row.processedBy}</span>
      ),
    },
  ];

  const reportCols: DataTableColumn<PAMMReport>[] = [
    {
      id: 'title',
      header: 'Report Title',
      accessor: row => <span className="text-xs font-medium">{row.title}</span>,
    },
    {
      id: 'type',
      header: 'Type',
      accessor: row => <StatusChip status={row.type} size="sm" />,
    },
    {
      id: 'period',
      header: 'Period',
      accessor: row => <span className="text-xs">{row.period}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <StatusChip status={row.status} size="sm" />,
    },
    {
      id: 'size',
      header: 'Size',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {row.fileSize ?? '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      accessor: row =>
        row.status === 'ready' ? (
          <Button variant="ghost" size="sm" className="h-6 px-2">
            <Download className="h-3.5 w-3.5" />
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-5 pb-6">
      <PageHeader title={fund.name} description={fund.description}>
        <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
          Back
        </Button>
        <Button
          size="sm"
          onClick={() => navigate(`${PAMM_ROUTES.PAMM_EDIT_FUND}/${id}`)}
        >
          <Edit className="h-3.5 w-3.5 mr-1.5" />
          Edit Fund
        </Button>
      </PageHeader>

      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatsCard
          title="Total AUM"
          value={formatCurrency(fund.totalAUM, fund.currency, true)}
          icon={TrendingUp}
        />
        <StatsCard
          title="Total Return"
          value={formatPercent(fund.totalReturn)}
          icon={Activity}
        />
        <StatsCard title="Investors" value={fund.totalInvestors} icon={Users} />
        <StatsCard
          title="Win Rate"
          value={`${fund.winRate}%`}
          icon={TrendingUp}
        />
      </div>

      {/* Status Badge */}
      <div className="flex items-center gap-3">
        <StatusChip status={fund.status} />
        <StatusChip status={fund.riskLevel} />
        <span className="text-xs text-muted-foreground">
          Leverage: {fund.leverage}
        </span>
        <span className="text-xs text-muted-foreground">
          Lock: {fund.lockPeriodDays}d
        </span>
      </div>

      {/* 7 Tabs */}
      <Tabs defaultValue="overview">
        <div className="overflow-x-auto pb-1">
          <TabsList className="inline-flex w-auto min-w-full sm:min-w-0">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="investors">
              Investors ({investors.length})
            </TabsTrigger>
            <TabsTrigger value="trades">Trades ({trades.length})</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoCard
              title="Fund Information"
              fields={[
                { label: 'Manager', value: fund.managerName },
                { label: 'Currency', value: fund.currency },
                { label: 'Created', value: formatDate(fund.createdAt) },
                { label: 'Last Updated', value: formatDate(fund.updatedAt) },
                {
                  label: 'Allocation Method',
                  value: fund.allocationMethod.replace('_', ' '),
                },
                { label: 'Lock Period', value: `${fund.lockPeriodDays} days` },
                {
                  label: 'Min Investment',
                  value: formatCurrency(fund.minimumInvestment, fund.currency),
                },
                {
                  label: 'Max Investment',
                  value: formatCurrency(fund.maximumInvestment, fund.currency),
                },
              ]}
            />
            <InfoCard
              title="Performance Metrics"
              fields={[
                {
                  label: 'Total Return',
                  value: (
                    <span className={getProfitColor(fund.totalReturn)}>
                      {formatPercent(fund.totalReturn)}
                    </span>
                  ),
                },
                {
                  label: 'Monthly Return',
                  value: (
                    <span className={getProfitColor(fund.monthlyReturn)}>
                      {formatPercent(fund.monthlyReturn)}
                    </span>
                  ),
                },
                {
                  label: 'Max Drawdown',
                  value: (
                    <span className="text-red-500">
                      {formatPercent(-fund.drawdown)}
                    </span>
                  ),
                },
                { label: 'Sharpe Ratio', value: fund.sharpeRatio.toFixed(2) },
                { label: 'Win Rate', value: `${fund.winRate}%` },
                {
                  label: 'Total Trades',
                  value: fund.totalTrades.toLocaleString(),
                },
                {
                  label: 'High Water Mark',
                  value: formatCurrency(fund.highWaterMark, fund.currency),
                },
                {
                  label: 'AUM',
                  value: formatCurrency(fund.totalAUM, fund.currency),
                },
              ]}
            />
            <InfoCard
              title="Fee Structure"
              fields={[
                { label: 'Performance Fee', value: `${fund.performanceFee}%` },
                {
                  label: 'Management Fee',
                  value: `${fund.managementFee}% /yr`,
                },
                { label: 'Entry Fee', value: `${fund.entryFee}%` },
                { label: 'Exit Fee', value: `${fund.exitFee}%` },
              ]}
            />
            <InfoCard
              title="Trading Symbols"
              fields={[
                {
                  label: 'Symbols',
                  value: (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {fund.tradingSymbols.map(s => (
                        <span
                          key={s}
                          className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded border border-blue-100"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  ),
                  span: true,
                },
              ]}
            />
          </div>
        </TabsContent>

        {/* Investors Tab */}
        <TabsContent value="investors" className="mt-4">
          <DataTable
            columns={investorCols}
            data={investors}
            showColumnVisibility={true}
            searchValue={searchInvestors}
            searchPlaceholder="Search investors..."
            onSearchChange={setSearchInvestors}
            onSearchReset={() => setSearchInvestors('')}
            showExportButton={true}
            exportButtonText="Export CSV"
            onExportClick={() => exportToCSV(investors, `fund_${id}_investors`)}
          />
        </TabsContent>

        {/* Trades Tab */}
        <TabsContent value="trades" className="mt-4">
          <DataTable
            columns={tradeCols}
            data={trades}
            searchValue={searchTrades}
            showColumnVisibility={true}
            searchPlaceholder="Search trades..."
            onSearchChange={setSearchTrades}
            onSearchReset={() => setSearchTrades('')}
            showExportButton={true}
            exportButtonText="Export CSV"
            onExportClick={() => exportToCSV(trades, `fund_${id}_trades`)}
          />
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="mt-4 space-y-4">
          <FundPerformanceChart
            data={MOCK_FUND_PERFORMANCE.data}
            title={`${fund.name} — NAV Performance`}
          />
          <InfoCard
            title="Performance Summary"
            columns={4}
            fields={[
              {
                label: 'Total Return',
                value: (
                  <span
                    className={getProfitColor(
                      MOCK_FUND_PERFORMANCE.summary.totalReturn
                    )}
                  >
                    {formatPercent(MOCK_FUND_PERFORMANCE.summary.totalReturn)}
                  </span>
                ),
              },
              {
                label: 'Max Drawdown',
                value: (
                  <span className="text-red-500">
                    {formatPercent(-MOCK_FUND_PERFORMANCE.summary.maxDrawdown)}
                  </span>
                ),
              },
              {
                label: 'Sharpe Ratio',
                value: MOCK_FUND_PERFORMANCE.summary.sharpeRatio.toFixed(2),
              },
              {
                label: 'Sortino Ratio',
                value: MOCK_FUND_PERFORMANCE.summary.sortinoRatio.toFixed(2),
              },
              {
                label: 'Win Rate',
                value: `${MOCK_FUND_PERFORMANCE.summary.winRate}%`,
              },
              {
                label: 'Profit Factor',
                value: MOCK_FUND_PERFORMANCE.summary.profitFactor.toFixed(2),
              },
              {
                label: 'Avg Win',
                value: formatCurrency(MOCK_FUND_PERFORMANCE.summary.averageWin),
              },
              {
                label: 'Avg Loss',
                value: formatCurrency(
                  MOCK_FUND_PERFORMANCE.summary.averageLoss
                ),
              },
            ]}
          />
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="mt-4">
          {reports.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No reports available for this fund.
            </p>
          ) : (
            <DataTable
              columns={reportCols}
              data={reports}
              showColumnVisibility={true}
              searchValue={searchReports}
              searchPlaceholder="Search reports..."
              onSearchChange={setSearchReports}
              onSearchReset={() => setSearchReports('')}
              showExportButton={true}
              exportButtonText="Export CSV"
              onExportClick={() => exportToCSV(reports, `fund_${id}_reports`)}
            />
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <DataTable
            columns={allocCols}
            data={allocations}
            showColumnVisibility={true}
            searchValue={searchAllocations}
            searchPlaceholder="Search allocation history..."
            onSearchChange={setSearchAllocations}
            onSearchReset={() => setSearchAllocations('')}
            showExportButton={true}
            exportButtonText="Export CSV"
            onExportClick={() => exportToCSV(allocations, `fund_${id}_history`)}
          />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Settings2 className="h-4 w-4" />
                Fund Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                To modify fund settings, use the Edit Fund page.
              </p>
              <Button
                onClick={() => navigate(`${PAMM_ROUTES.PAMM_EDIT_FUND}/${id}`)}
              >
                <Edit className="h-3.5 w-3.5 mr-1.5" />
                Edit Fund Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
